/**
 * Shell —— App Shell（顶部栏 + 主导航 + 设置抽屉）。
 * 导航顺序 / 图标 / 路由冻结，页面不得增删改序（design-contract.md）。
 */
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NavLink, useNavigate } from 'react-router-dom';
import { avatarText, fetchMe, tokenStore } from '../api/auth';
import { deviceKeys, devicesApi } from '../api/devices';
import { Icon } from './Icon';
import { Sheet, toast } from './ui';

const NAV = [
  { to: '/console', label: '我的设备', icon: 'layout-grid' },
  { to: '/community', label: '分享社区', icon: 'users' },
  { to: '/mcp', label: 'MCP 管理', icon: 'plug' },
  { to: '/integrations', label: '第三方接入', icon: 'cloud' },
];

export function Shell({ children }: { children: ReactNode }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const devQuery = useQuery({ queryKey: deviceKeys.list, queryFn: devicesApi.list });

  const user = meQuery.data?.user;
  const online = devQuery.data?.online ?? 0;
  const total = devQuery.data?.total ?? 0;

  const logout = (): void => {
    tokenStore.clear();
    qc.clear();
    toast.ok('已退出登录');
    navigate('/login', { replace: true });
  };

  return (
    <div className="app">
      <div className="shell-wrap">
        <header className="shell">
          <div className="shell__brand">
            <span className="shell__mark">
              <Icon name="cpu" size={18} />
            </span>
            <span className="shell__name">智能家居控制台</span>
          </div>
          <div className="shell__user">
            <span className="avatar">{user ? avatarText(user.nickname) : '·'}</span>
            <span className="shell__meta">
              <span className="shell__nick">{user?.nickname ?? '未登录'}</span>
              <span className="shell__online">
                <i className="dot dot--live" />
                {online}/{total} 在线
              </span>
            </span>
            <button
              className="btn btn--icon"
              type="button"
              aria-label="设置"
              onClick={() => setSettingsOpen(true)}
            >
              <Icon name="settings" size={18} />
            </button>
          </div>
        </header>

        <nav className="shell-nav" aria-label="主导航">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => 'nav-item' + (isActive ? ' is-active' : '')}
            >
              <Icon name={n.icon} size={14} />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="app-content">{children}</main>

      <Sheet open={settingsOpen} onClose={() => setSettingsOpen(false)} title="账号与设置">
        <div className="card card--flat" style={{ marginBottom: 12 }}>
          <div className="row" style={{ gap: 12 }}>
            <span className="avatar avatar--lg">{user ? avatarText(user.nickname) : '·'}</span>
            <span className="stack" style={{ gap: 2 }}>
              <strong>{user?.nickname ?? '—'}</strong>
              <span className="t-sm muted">{user?.email ?? '—'}</span>
              <span className="t-xs dim">设备配额 {total}/10 台</span>
            </span>
          </div>
        </div>
        <div className="list">
          <NavLink className="list__row" to="/mcp" onClick={() => setSettingsOpen(false)}>
            <span className="list__icon">
              <Icon name="plug" size={16} />
            </span>
            <span className="list__main">
              <span className="list__title">MCP 接入与语音绑定</span>
              <span className="list__sub">查看自建接入地址 / 小智官方语音接入状态</span>
            </span>
            <Icon name="chevron-right" size={16} />
          </NavLink>
          <NavLink className="list__row" to="/integrations" onClick={() => setSettingsOpen(false)}>
            <span className="list__icon">
              <Icon name="cloud" size={16} />
            </span>
            <span className="list__main">
              <span className="list__title">第三方账号</span>
              <span className="list__sub">涂鸦云 / 米家账号的绑定与同步</span>
            </span>
            <Icon name="chevron-right" size={16} />
          </NavLink>
        </div>
        <button className="btn btn--danger btn--block" type="button" style={{ marginTop: 16 }} onClick={logout}>
          <Icon name="log-out" size={16} />
          退出登录
        </button>
      </Sheet>
    </div>
  );
}
